import { useAuth } from '@clerk/clerk-react'
import { Navigate, useSearchParams } from 'react-router-dom'

import { AdminChecks } from '@/components/admin/AdminChecks'
import { AdminInbox } from '@/components/admin/AdminInbox'
import { AdminReports } from '@/components/admin/AdminReports'
import { useMe } from '@/context/MeProvider'
import { cn } from '@/lib/cn'
import { rememberAuthRedirect, withRedirect } from '@/lib/redirect'

const TABS = [
  { label: 'Reports', value: 'reports' },
  { label: 'Checks', value: 'checks' },
  { label: 'Support', value: 'support' },
] as const

type Tab = (typeof TABS)[number]['value']

function parseTab(value: string | null): Tab {
  if (value === 'support' || value === 'checks') return value
  return 'reports'
}

export function AdminPage() {
  const { isLoaded, isSignedIn } = useAuth()
  const { me, loading: meLoading } = useMe()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseTab(searchParams.get('tab'))

  if (!isLoaded) {
    return null
  }
  if (!isSignedIn) {
    rememberAuthRedirect('/admin')
    return <Navigate to={withRedirect('/sign-in', '/admin')} replace />
  }

  const denied = !meLoading && me !== null && me.role !== 'admin'

  return (
    <main
      id="main"
      className="max-w-site px-margin-mobile md:px-margin-desktop mx-auto w-full flex-grow pt-24 pb-24"
    >
      <div className="space-y-8">
        <h1 className="text-headline-lg-mobile text-fg md:text-headline-lg">
          Admin
        </h1>

        {meLoading && me === null && (
          <p className="font-body text-body-md text-fg-muted">Loading…</p>
        )}

        {denied && (
          <p className="font-body text-body-md text-fg-muted">
            You don&apos;t have access.
          </p>
        )}

        {me?.role === 'admin' && (
          <>
            <div
              role="tablist"
              aria-label="Admin"
              className="flex flex-wrap gap-2"
            >
              {TABS.map((option) => {
                const selected = tab === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => {
                      setSearchParams(
                        option.value === 'reports' ? {} : { tab: option.value },
                        { replace: true },
                      )
                    }}
                    className={cn(
                      'font-body text-label-caps min-h-11 rounded-full border px-4 uppercase transition-colors',
                      selected
                        ? 'border-primary-container/30 bg-primary-container/15 text-primary'
                        : 'border-glass-border text-fg-muted hover:text-fg bg-white/5',
                    )}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>

            <div role="tabpanel">
              {tab === 'reports' && <AdminReports />}
              {tab === 'checks' && <AdminChecks />}
              {tab === 'support' && <AdminInbox />}
            </div>
          </>
        )}
      </div>
    </main>
  )
}
